<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
  >
    <header :class="$style.header">
      <BaseHeading size="h3" as="h2" :class="$style.heading">
        {{ data?.fields?.header }}
      </BaseHeading>
    </header>
    <div :class="$style.grid">
      <div
        v-for="group in downloadableGroups()?.slice(0, numberToShow)"
        :key="group?.sys?.id"
        :class="[
          $style.wallpaperWrap,
          activeId === group?.sys?.id && $style.isActive,
        ]"
        @click="onWallpaperClick(group?.sys?.id)"
      >
        <WithBackdrop
          :backdrop="group?.fields?.thumbnail"
          :backdrop-class="$style.backdrop"
          :class="$style.wallpaper"
        >
          <AspectRatio ratio="1x1">
            <div :class="$style.infoWrapper">
              <div :class="$style.info">
                <h3 :class="$style.infoHeader">{{ group?.fields?.header }}</h3>
                <h6 :class="$style.infoSubheader">
                  {{ group?.fields?.subheader }}
                </h6>
                <BaseCollapse
                  fade-in
                  offset-content="translate3d(0, -50%, 0)"
                  :in-prop="activeId === group?.sys?.id"
                >
                  <ul :class="[$style.links, $style.oneColumns]">
                    <li
                      v-for="asset in group?.fields?.downloadableAssets"
                      :key="asset?.sys?.id"
                    >
                      <a
                        v-if="asset"
                        :class="$style.link"
                        :href="asset?.fields?.cta?.fields?.link"
                        target="_blank"
                        @click="handleClick(asset)"
                      >
                        {{ asset?.fields?.cta?.fields?.label }}
                      </a>
                    </li>
                  </ul>
                  <ul :class="[$style.links, $style.twoColumns]">
                    <li
                      v-for="asset in group?.fields?.downloadableAssets"
                      :key="asset?.sys?.id"
                    >
                      <a
                        v-if="asset"
                        :class="$style.link"
                        :href="asset?.fields?.cta?.fields?.link"
                        target="_blank"
                        @click="handleClick(asset)"
                        >{{ asset?.fields?.cta?.fields?.label }}</a
                      >
                    </li>
                  </ul>
                </BaseCollapse>
              </div>
            </div>
          </AspectRatio>
        </WithBackdrop>
      </div>
    </div>

    <div v-if="loadMore() || viewAll()" :class="$style.ctas">
      <div />
      <div :class="$style.cta">
        <CallToAction
          v-if="loadMore() && computedHasMoreToShow"
          :cta="loadMore()"
          @cta-click="onRequestMore"
        />
      </div>
      <div :class="$style.cta">
        <CallToAction
          v-if="viewAll()"
          :cta="viewAll()"
          :class="$style.viewAll"
        />
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import type {
  TypeContentBlockWithoutUnresolvableLinksResponse,
  TypeDownloadableAssetWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

const activeId = ref('')
const numberToShow = ref(12)
//   computed: {
//     computedEntryTitle() {
//       return this.data?.entryTitle
//     },
//     computedBackdrop() {
//       return this.data?.backdrop
//     },
//     computedCssOverride() {
//       return this.data?.cssOverride
//     },
//     computedHeader() {
//       return this.data?.header
//     },
function loadMore() {
  return props.data?.fields?.ctas?.[0]
}

function viewAll() {
  return props.data?.fields?.ctas?.[1]
}

function downloadableGroups() {
  return props?.data?.fields?.references?.filter(instanceOfDownloadableGroup)
}

const computedHasMoreToShow = computed(() => {
  return (downloadableGroups()?.length || 0) > numberToShow.value
})

const onRequestMore = () => {
  numberToShow.value = numberToShow.value + 12
}

const onWallpaperClick = (id: string) => {
  if (activeId.value === id) return (activeId.value = '')
  activeId.value = id
}

const handleClick = (
  asset: TypeDownloadableAssetWithoutUnresolvableLinksResponse,
) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'ga4.trackEvent',
    eventName: 'resource_download',
    eventParams: {
      file_name: asset?.fields?.cta?.fields?.link
        ? new URL(asset.fields.cta.fields.link, 'https://placeholder').pathname
        : '',
      file_extension: asset?.fields?.cta?.fields?.link
        ?.split('/')
        ?.pop()
        ?.split('.')
        ?.pop()
        ?.toLowerCase(),
      link_text: asset?.fields?.cta?.fields?.label || '',
      link_url: asset?.fields?.cta?.fields?.link || '',
      resource_type: 'marketing material',
      resource_name: asset?.fields?.entryTitle,
      set_name: props.data?.fields?.entryTitle,
    },
  })
}
</script>

<style lang="scss" module>
.config {
  --wrap-padding--base: 30px 0;
  --wrap-padding--sm: 40px 0;
  --wrap-padding--md: 60px 0 70px;
  --wrap-padding: var(--wrap-padding--base);

  --grid-template-columns--base: 1fr 1fr;
  --grid-template-columns--sm: 1fr 1fr 1fr;
  --grid-template-columns--md: 1fr 1fr 1fr;
  --grid-template-columns--lg: 1fr 1fr 1fr 1fr;

  --links-grid-template-columns--base: 1fr;
  --links-grid-template-columns--md: 1fr 1fr;
  --links-grid-template-columns: var(--links-grid-template-columns--base);

  --links-grid-gap--base: 0.5em;
  --links-grid-gap--md: 1em;
  --links-grid-gap: var(--links-grid-gap--base);

  --grid-template-columns: var(--grid-template-columns--base);

  --container-max-width: 1440px;

  @include media('sm') {
    --wrap-padding: var(--wrap-padding--sm);
    --grid-template-columns: var(--grid-template-columns--sm);
  }

  @include media('md') {
    --wrap-padding: var(--wrap-padding--md);
    --grid-template-columns: var(--grid-template-columns--md);
    --links-grid-template-columns: var(--links-grid-template-columns--md);
    --links-grid-gap: var(--links-grid-gap--md);
  }

  @include media('lg') {
    --grid-template-columns: var(--grid-template-columns--lg);
  }
}

.wrap {
  padding: var(--wrap-padding);
}

.header {
  text-align: center;
  margin-bottom: 2em;
}

.grid {
  display: grid;
  grid-template-columns: var(--grid-template-columns);
}

.wallpaperWrap {
  cursor: pointer;
}

.wallpaper {
  backface-visibility: hidden;

  .wallpaperWrap.isActive & {
    filter: grayscale(100);
  }

  .wallpaperWrap.isActive &::before {
    opacity: 1;
  }

  .wallpaperWrap.isActive &::after {
    // background: linear-gradient(135deg, #ff6000 0%, #e83411 100%);
    opacity: 0.33;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  &::before {
    transition: opacity 500ms ease;
    // background: linear-gradient(135deg, #ff6000 0%, #e83411 100%);
    background: rgba(black, 0.5);
    opacity: 0;
    mix-blend-mode: hard-light;
    backface-visibility: hidden;
    z-index: 1;
  }

  &::after {
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.75) 0%,
      rgba(0, 0, 0, 0.0001) 66%
    );
  }
}

.links {
  @include list-unstyled();
  @include fluid-type(14px, 16px);
  text-align: center;

  display: grid;
  gap: var(--links-grid-gap);
  grid-template-columns: var(--links-grid-template-columns);
  position: relative;
  padding: 2em 0 0;
  z-index: 2;

  &.twoColumns {
    display: none;
    @include media('md') {
      display: grid;
    }
  }

  &.oneColumns {
    @include media('md') {
      display: none;
    }
  }
}

.link {
  color: var(--white);
  font-weight: bold;
  display: inline-block;
}

.infoWrapper {
  align-items: end;
  display: flex;
  height: 100%;
  padding: 5%;
}

.info {
  color: var(--white);
  text-align: center;
  text-shadow: 0 1px 2px rgba(black, 0.5);
  text-transform: capitalize;
  z-index: 2;

  top: unset !important; // overwrite aspect ratio
  bottom: 5%;

  letter-spacing: 0.1em;
  transition:
    transform 500ms ease,
    bottom 500ms ease;
  transform: translate3d(0, 0, 0);
  padding: 1em;

  .wallpaperWrap:hover & {
    transform: translate3d(0, -15%, 0);
    // letter-spacing: 0.15em;
  }

  .wallpaperWrap.isActive & {
    bottom: 50%;
    transform: translate3d(0, 50%, 0);
  }
}

.infoHeader {
  @include fluid-type(16px, 18px);
  line-height: 1;
  margin-bottom: 0.25em;
}

.infoSubheader {
  font-weight: normal;
}

.ctas {
  display: grid;
  gap: 20px;
  text-align: center;
  padding: 40px 15px 0;
  position: relative;
  max-width: 1440px;
  margin: 0 auto;

  @include media('sm') {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
</style>
